import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import { toastSuccess, toastError } from "../../utils/alertHelper";
import CommonTable from "../../components/commonComponent/CommanTable";
import CommonModal from "../../components/commonComponent/CommonModal";
import { FaPencilAlt } from "react-icons/fa";

const solarPartnerFields = [
  { type: "heading", label: "Basic Information" },
  { name: "clientId", label: "Client ID", type: "text", placeholder: "Client ID" },
  { name: "companyName", label: "Company Name", type: "text", placeholder: "Company Name" },
  { name: "firstName", label: "First Name", type: "text", placeholder: "First Name" },
  { name: "lastName", label: "Last Name", type: "text", placeholder: "Last Name" },
  { name: "city", label: "City", type: "text", placeholder: "City" },
  { name: "state", label: "State", type: "text", placeholder: "State" },
  { name: "country", label: "Country", type: "text", placeholder: "Country" },
  { type: "heading", label: "Contact Information" },
  { name: "mobileNumber", label: "Mobile Number", type: "text", placeholder: "Mobile Number" },
  { name: "email", label: "Email", type: "email", placeholder: "Email" },
  { type: "heading", label: "Business Information" },
  { name: "gstNumber", label: "GST Number", type: "text", placeholder: "GST Number" },
];

const logColumns = [
  { key: "srNo", label: "Sr.no:" },
  { key: "moduleName", label: "Module Name" },
  { key: "action", label: "Action" },
  { key: "actionBy", label: "Action By" },
  { key: "dateTime", label: "Date & time" },
  { key: "whatAction", label: "What Action" },
];

const billingColumns = [
  { key: "srNo", label: "Sr.no:" },
  { key: "invoiceNumber", label: "Invoice Number" },
  { key: "package", label: "Package" },
  { key: "company", label: "Company" },
  { key: "paymentDate", label: "Payment date" },
  { key: "nextPaymentDate", label: "Next Payment date" },
  { key: "transactionId", label: "Transaction Id" },
  { key: "gstNumber", label: "GST Number" },
  { key: "amount", label: "Amount" },
  { key: "paymentMode", label: "Payment Mode" },
  { key: "actions", label: "Action" },
];

function SolarView() {
  const { id } = useParams();
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logRows, setLogRows] = useState([]);
  const [billingRows, setBillingRows] = useState([]);
  const [billingSearch, setBillingSearch] = useState("");

  const filteredBillingRows = billingRows.filter(row =>
    Object.values(row)
      .join(" ")
      .toLowerCase()
      .includes(billingSearch.toLowerCase())
  );

  useEffect(() => {
    async function fetchPartner() {
      try {
        const res = await axios.get(`/api/user/${id}`);
        const data = res.data;
        const user = data.user;

        setModalData({
          clientId: user.memberId || "",
          companyName: user.business?.name || "",
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          city:
            user.city?.name ||
            (typeof user.city === "string" ? user.city : ""),
          state:
            user.state?.name ||
            (typeof user.state === "string" ? user.state : ""),
          country:
            user.country?.name ||
            (typeof user.country === "string" ? user.country : "India"),
          mobileNumber: user.mobileNumber || "",
          email: user.email || "",
          gstNumber: user.business?.gstNumber || "",
          plan: user.plan?.plan?.name || user.plan?.name || "Basic",
          companyStatus: user.status || "",
        });

        setLogRows([
          {
            srNo: 1,
            moduleName: "User",
            action: "Fetched",
            actionBy: user.email || "",
            dateTime: user.createdAt ? new Date(user.createdAt).toLocaleString() : "",
            whatAction: "User Fetched Successfully",
          },
        ]);

        setBillingRows(
          Array.isArray(user.billings)
            ? user.billings.map((bill, idx) => ({
                srNo: idx + 1,
                invoiceNumber: bill.billingNumber || "",
                package: bill.plan?.name || "",
                company: user.business?.name || "",
                paymentDate: bill.startDate
                  ? new Date(bill.startDate).toLocaleDateString()
                  : "",
                nextPaymentDate: bill.endDate
                  ? new Date(bill.endDate).toLocaleDateString()
                  : "",
                transactionId: bill.transactionId || "",
                gstNumber: user.business?.gstNumber || "",
                amount: bill.amount ? `₹${bill.amount}` : "",
                paymentMode: bill.paymentStatus || "",
              }))
            : []
        );

        toastSuccess("User details fetched!");
      } catch (e) {
        setModalData({});
        setLogRows([]);
        setBillingRows([]);
        toastError("Failed to fetch user details.");
      }
    }
    if (id) fetchPartner();
  }, [id]);

  const handleEditClick = () => {
    setErrors({});
    setShowModal(true);
  };

  const handleSubmit = () => {
    let newErrors = {};
    [
      "clientId",
      "companyName",
      "firstName",
      "lastName",
      "city",
      "state",
      "country",
      "mobileNumber",
      "email",
      "gstNumber",
    ].forEach(field => {
      if (!modalData[field]) newErrors[field] = "Required";
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setShowModal(false);
  };

  return (
    <div className="container mt-4" style={{ maxWidth: 1200 }}>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="fw-bold text-dark">Solar Partner Details</h2>
        <div>
          <a href="/dashboard/solar" className="text-dark text-decoration-none">
            &larr; Solar Partner
          </a>
        </div>
      </div>
      <div
        className="mb-4 mt-4"
        style={{
          background: "#fff",
          border: "1px solid #e3e6ef",
          borderRadius: "12px",
          padding: "24px 18px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="fw-bold fs-4 text-dark">Basic info</div>
          <div>
            <button
              className="btn btn-light border rounded-circle shadow-sm"
              title="Edit"
              onClick={handleEditClick}
            >
              <FaPencilAlt />
            </button>
          </div>
        </div>
        <div className="d-flex align-items-center">
          <div style={{ minWidth: 220 }}>
            <div className="fw-semibold mb-1 text-dark">Basic Information</div>
            <div className="d-flex">
              <div style={{ minWidth: 110 }}>
                <div className="mb-1">
                  <span className="fw-bold">Client Id:</span>
                </div>
                <div className="mb-1">
                  <span className="fw-bold">Name:</span>
                </div>
                <div className="mb-1">
                  <span className="fw-bold">Company Name:</span>
                </div>
                <div className="mb-1">
                  <span className="fw-bold">Company Status:</span>
                </div>
                <div className="mb-1">
                  <span className="fw-bold">Current Plan:</span>
                </div>
              </div>
              <div style={{ minWidth: 110 }}>
                <div className="mb-1">{modalData.clientId}</div>
                <div className="mb-1">
                  {modalData.firstName} {modalData.lastName}
                </div>
                <div className="mb-1">{modalData.companyName}</div>
                <div className="mb-1">{modalData.companyStatus || ""}</div>
                <div className="mb-1">{modalData.plan || ""}</div>
              </div>
            </div>
          </div>
          <div
            style={{
              borderLeft: "2px solid #e3e6ef",
              height: 100,
              margin: "0 24px",
            }}
          ></div>
          <div style={{ minWidth: 120 }}>
            <div className="mb-1">
              <span className="fw-bold">City:</span> {modalData.city}
            </div>
            <div className="mb-1">
              <span className="fw-bold">State:</span> {modalData.state}
            </div>
            <div className="mb-1">
              <span className="fw-bold">Country:</span> {modalData.country}
            </div>
          </div>
          <div
            style={{
              borderLeft: "2px solid #e3e6ef",
              height: 100,
              margin: "0 24px",
            }}
          ></div>
          <div style={{ minWidth: 180 }}>
            <div className="fw-semibold mb-1 text-dark">
              Contact Information
            </div>
            <div className="mb-1">
              <span className="fw-bold">Mobile Number:</span>{" "}
              {modalData.mobileNumber}
            </div>
            <div className="mb-1">
              <span className="fw-bold">Email:</span> {modalData.email}
            </div>
          </div>
        </div>
      </div>
      <div>
        <div className="fw-bold fs-4 mb-2 text-dark">Log</div>
        <CommonTable
          columns={logColumns}
          data={logRows}
          itemsPerPageOptions={[15, 20, 25, 30]}
          defaultItemsPerPage={15}
          tableClassName="mb-4"
          bordered
        />
      </div>
      <div className="mt-4">
        <div className="fw-bold fs-4 mb-2 text-dark">Billing Details</div>
        <div className="d-flex justify-content-end mb-2">
          <div className="input-group" style={{ maxWidth: 260 }}>
            <span className="input-group-text bg-white">
              <i className="fas fa-search text-muted"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Type to search"
              value={billingSearch}
              onChange={e => setBillingSearch(e.target.value)}
              style={{ boxShadow: "none" }}
            />
          </div>
        </div>
        <CommonTable
          columns={billingColumns}
          data={filteredBillingRows}
          itemsPerPageOptions={[15]}
          defaultItemsPerPage={15}
          tableClassName="mb-4"
          bordered
        />
      </div>
      <CommonModal
        visible={showModal}
        title="Edit Solar Partner"
        fields={solarPartnerFields}
        values={modalData}
        onChange={setModalData}
        onCancel={() => setShowModal(false)}
        onSubmit={handleSubmit}
        submitLabel={isSubmitting ? "Updating..." : "Update"}
        cancelLabel="Cancel"
        requiredFields={[
          "clientId",
          "companyName",
          "firstName",
          "lastName",
          "city",
          "state",
          "country",
          "mobileNumber",
          "email",
          "gstNumber",
        ]}
        errors={errors}
        disabled={isSubmitting}
      />
    </div>
  );
}

export default SolarView;